import fetch from 'isomorphic-fetch';
import { apiQueryShopsByIds } from '@lib/apiQueries';
import type { ShopsByIdsQuery } from '@graphql/shared/shops-by-ids.generated';
import { isRecord } from './data/contracts';
import { parseUserData } from './user-contract';
import type { UserData } from './user-contract';
import type { Locale } from './locale';

function isShopsResult(value: unknown): value is { data: ShopsByIdsQuery } {
  return isRecord(value) && isRecord(value.data)
    && Array.isArray(value.data.shopsByIds);
}

export const isUserLoggedIn = (user: UserData | null | undefined): boolean => Boolean(user?.collectorNumber);

export const saveOffer = (offerId: string, callback = () => {}) => {
  const fetchOptions: RequestInit = {
    method: 'post',
    credentials: 'include',
    mode: 'same-origin',
    headers: {
      data: JSON.stringify({
        id: offerId,
      }),
    },
  };
  const fetchUrl = '/api/fav/offer';

  return fetch(fetchUrl, fetchOptions).then((response) => {
    if (!response.ok) {
      console.error(`Unable to save offer (${response.status})`);
      return;
    }
    callback();
  });
};

export const saveShop = (shopId: string, callback = () => {}) => {
  const fetchOptions: RequestInit = {
    method: 'post',
    credentials: 'include',
    mode: 'same-origin',
    headers: {
      data: JSON.stringify({
        id: shopId,
      }),
    },
  };
  const fetchUrl = '/api/fav/shop';

  return fetch(fetchUrl, fetchOptions).then((response) => {
    if (!response.ok) {
      console.error(`Unable to save shop (${response.status})`);
      return;
    }
    callback();
  });
};

export const saveInterrupt = (
  shopId: string,
  dateAdded: string,
  callback = () => {},
) => {
  const fetchOptions: RequestInit = {
    method: 'post',
    credentials: 'include',
    mode: 'same-origin',
    headers: {
      data: JSON.stringify({
        id: shopId,
        dateAdded,
      }),
    },
  };
  const fetchUrl = '/api/fav/interrupt';

  return fetch(fetchUrl, fetchOptions).then((response) => {
    if (!response.ok) throw new Error(`Unable to save interrupt (${response.status})`);
    callback();
  });
};

export const removeOfferById = (offerId: string, callback = () => {}) => {
  const fetchOptions: RequestInit = {
    method: 'delete',
    credentials: 'include',
    mode: 'same-origin',
  };
  const fetchUrl = `/api/fav/offer/${offerId}`;

  return fetch(fetchUrl, fetchOptions).then((response) => {
    if (!response.ok) {
      console.error(`Unable to remove offer (${response.status})`);
      return;
    }
    callback();
  });
};

export const removeShopById = (shopId: string, callback = () => {}) => {
  const fetchOptions: RequestInit = {
    method: 'delete',
    credentials: 'include',
    mode: 'same-origin',
  };
  const fetchUrl = `/api/fav/shop/${shopId}`;

  return fetch(fetchUrl, fetchOptions).then((response) => {
    if (!response.ok) {
      console.error(`Unable to remove shop (${response.status})`);
      return;
    }
    callback();
  });
};

export const removeInterruptById = (shopId: string, callback = () => {}) => {
  const fetchOptions: RequestInit = {
    method: 'delete',
    credentials: 'include',
    mode: 'same-origin',
  };
  const fetchUrl = `/api/fav/interrupt/${shopId}`;

  return fetch(fetchUrl, fetchOptions).then((response) => {
    if (!response.ok) throw new Error(`Unable to remove interrupt (${response.status})`);
    callback();
  });
};

export async function getFullUserData(locale: Locale = 'en') {
  let userData: UserData = {};
  let userShopsData: NonNullable<ShopsByIdsQuery['shopsByIds']> = [];

  const apiOptions: RequestInit = {
    method: 'GET',
    credentials: 'include',
    mode: 'same-origin',
  };

  try {
    const userRes = await fetch('/api/user', apiOptions);
    if (userRes.ok) {
      const response: unknown = await userRes.json();
      userData = parseUserData(response);
    }

    if (userData?.shops?.length) {
      const favShopIds = userData.shops.map((shop) => shop.id);
      const shopsRes: unknown = await apiQueryShopsByIds(favShopIds, locale);
      if (isShopsResult(shopsRes) && shopsRes.data.shopsByIds) {
        userShopsData = shopsRes.data.shopsByIds;
      }
    }
  } catch (err) {
    console.error('Error fetching user session data:', err);
  }

  return { userData, userShopsData };
}
